import {
  useElement,
  useState,
  useStaleLayout,
  useRect,
  useEffect,
  useModel,
} from '@nebula.js/stardust';
import picassojs from 'picasso.js';
import picassoQ from 'picasso-plugin-q';
import picassoHammer from 'picasso-plugin-hammer';
import * as d3 from 'd3';
import lightOrDark from 'utils/lightordark';
import { easeExpOut as ease } from 'd3-ease';
import { timer } from 'd3-timer';
import { interpolate } from 'd3-interpolate';

let transition = null;
const stopTransition = () => {
  if (transition) {
    transition.stop();
    transition = null;
  }
};

const findLogo = (name) => {
  switch (name) {
    case 'CBC News, Current Affairs and Local (CBC News (Network/National programming), CBC News Current Affairs, CBC News Local Services)':
      return 'CBC News, Current Affairs and Local';
    case 'Postmedia Network Inc.':
      return 'Postmedia';
    case 'APTN':
      return 'APTN National News';
    case 'Radio-Canada (13 multi-platform production centers (digital, radio, TV): Montreal, Quebec City, Sherbrooke, Trois-Rivières, Saguenay, Rimouski, Ottawa-Gatineau, Toronto, Moncton, Regina, Winnipeg, Edmonton, Vancouver, 7 radio stations: Rouyn, Matane, Sept-Iles, Sudbury, Windsor, Halifax, Charlottetown)':
      return 'radio-canada';
    default:
      return name;
  }
};

export default function supernova() {
  const picasso = picassojs();
  picasso.use(picassoQ);
  picasso.use(picassoHammer);

  picasso.component('logos', {
    require: ['renderer', 'resolver'],
    render({ data }) {
      const { items } = this.resolver.resolve({
        data,
        settings: this.settings.settings,
      });

      const filteredItems = items.filter((item) => ['APTN', 'Toronto Star', 'The Tyee', 'APTN National News', 'CBC News, Current Affairs and Local', 'La Presse', 'Saltwire Network', 'Radio-Canada (13 multi-platform production centers (digital, radio, TV): Montreal, Quebec City, Sherbrooke, Trois-Rivières, Saguenay, Rimouski, Ottawa-Gatineau, Toronto, Moncton, Regina, Winnipeg, Edmonton, Vancouver, 7 radio stations: Rouyn, Matane, Sept-Iles, Sudbury, Windsor, Halifax, Charlottetown)', 'Edmonton Journal', 'CBC News, Current Affairs and Local (CBC News (Network/National programming), CBC News Current Affairs, CBC News Local Services)', 'Postmedia', 'The Canadian Press', 'The Chronicle Herald', 'The Globe and Mail', 'The Hamilton Spectator', 'TVO', 'Winnipeg Free Press'].includes(item.data.label));
      const containerEl = this.renderer.element().parentNode;
      const oldImgEls = containerEl.querySelectorAll('img');
      for (let i = 0; i < oldImgEls.length; i++) {  //eslint-disable-line
        oldImgEls[i].remove();
      }

      for (let i = 0; i < filteredItems.length; i++) {  //eslint-disable-line
        const item = filteredItems[i];
        const size = item.size * Math.min(this.rect.width, this.rect.height) * 0.15;
        const scale = 0.45;
        const imgEl = document.createElement('img');
        imgEl.src = `./assets/logos/${findLogo(item.data.label)}.svg`;
        imgEl.width = size * scale;
        imgEl.style.position = 'absolute';
        imgEl.style.opacity = '0.7';
        imgEl.style.left = `${(item.xScale(item.data.x.value) * this.rect.width) - (scale * (size / 2)) + 51}px`;
        imgEl.style.top = `${(item.yScale(item.data.y.value) * this.rect.height) - (scale * (size / 2))}px`;
        containerEl.append(imgEl);
      }
      return [];
    },
  });

  picasso.component('diagonal', {
    require: ['renderer', 'resolver'],
    render({ data }) {
      const { items } = this.resolver.resolve({
        data,
        settings: this.settings.settings,
      });

      const x1 = items[0].xScale(0) * this.rect.width;
      const y1 = (items[0].yScale(0)) * (this.rect.height);

      const x2 = items[0].xScale(1) * this.rect.width;
      const y2 = (items[0].yScale(1)) * (this.rect.height);

      return [
        {
          type: 'line',
          stroke: '#939799',
          strokeWidth: 1,
          x1,
          y1,
          x2,
          y2,
        },
      ];
    },
  });

  return {
    qae: {
      properties: {
        qHyperCubeDef: {
          qDimensions: [],
          qMeasures: [],
          qInitialDataFetch: [{ qWidth: 5, qHeight: 2000 }],
          qSuppressZero: true,
          qSuppressMissing: true,
        },
        showTitles: true,
        title: '',
        subtitle: '',
        footnote: '',
      },
      data: {
        targets: [
          {
            path: '/qHyperCubeDef',
            dimensions: {
              min: 1,
              max: 2,
            },
            measures: {
              min: 1,
              max: 3,
            },
          },
        ],
      },
    },
    component() {
      const element = useElement();
      const layout = useStaleLayout();
      const rect = useRect();
      const model = useModel();

      const genderToggled = !(layout.qHyperCube.qMeasureInfo[0].qFallbackTitle === '=Only({<[Results - 2022]={"Disaggregate"}, [Name of media outlet for search - 2022]=>} ([Total Employees Excl Unknown - 2022] - [White Total - 2022]) / [Total Employees Excl Unknown - 2022])');

      const [instance, setInstance] = useState();
      const [prevData, setPrevData] = useState([]);

      useEffect(() => {
        const p = picasso.chart({
          element,
          data: [],
          settings: {},
        });

        setInstance(p);

        return () => {
          p.destroy();
        };
      }, []);

      useEffect(() => {
        if (!instance) {
          return;
        }

        if (transition) {
          stopTransition();
        }

        const duration = 500;

        // Selected Outlet
        const selectedOutlet = layout.qHyperCube.qDataPages[0].qMatrix.filter((row) => row[0].qState === 'S').map((row) => row[0].qText);

        transition = timer((elapsed) => {
          const t = Math.min(1, ease(elapsed / duration));

          instance.update({
            data: [
              {
                type: 'q',
                key: 'qHyperCube',
                data: layout.qHyperCube,
              },
            ],
            settings: {
              scales: {
                x: {
                  data: {
                    field: 'qMeasureInfo/0',
                  },
                  expand: 0.05,
                  min: 0,
                  max: 1.1,
                  ticks: { values: [0, 0.25, 0.5, 0.75, 1] },
                },
                y: {
                  data: {
                    field: 'qMeasureInfo/1',
                  },
                  expand: 0.05,
                  min: 0,
                  max: 1.1,
                  invert: true,
                  ticks: { values: [0, 0.25, 0.5, 0.75, 1] },
                },
                r: {
                  data: {
                    field: 'qMeasureInfo/2',
                  },
                },
              },
              components: [
                {
                  key: 'x-axis',
                  type: 'axis',
                  scale: 'x',
                  dock: 'bottom',
                },
                {
                  key: 'y-axis',
                  type: 'axis',
                  scale: 'y',
                  dock: 'left',
                },
                {
                  type: 'diagonal',
                  data: {
                    extract: {
                      field: 'qDimensionInfo/0',
                      props: {
                        x: {
                          field: 'qMeasureInfo/0',
                        },
                        y: {
                          field: 'qMeasureInfo/1',
                        },
                      },
                    },
                  },
                  settings: {
                    xScale: (d) => d.resources.scale('x'),
                    yScale: (d) => d.resources.scale('y'),
                  },
                },
                {
                  type: 'logos',
                  data: {
                    extract: {
                      field: 'qDimensionInfo/0',
                      props: {
                        x: { field: 'qMeasureInfo/0' },
                        y: { field: 'qMeasureInfo/1' },
                        size: { field: 'qMeasureInfo/2' },
                      },
                    },
                  },
                  settings: {
                    x: { scale: 'x', ref: 'x' },
                    y: { scale: 'y', ref: 'y' },
                    size: {
                      fn: (d) => {
                        const domain = d3.extent(d.data.items.map((datum) => datum.size.value));
                        domain[0] = 1;
                        const size = d3.scaleLog().domain(domain).range([0, 1])(d.datum.size.value);
                        return size;
                      },
                    },
                    xScale: (d) => d.resources.scale('x'),
                    yScale: (d) => d.resources.scale('y'),
                  },
                },
                {
                  key: 'p',
                  type: 'point',
                  data: {
                    extract: {
                      field: 'qDimensionInfo/0',
                      props: {
                        x: { field: 'qMeasureInfo/0' },
                        y: { field: 'qMeasureInfo/1' },
                        size: { field: 'qMeasureInfo/2' },
                        outlet_pretty_name: { field: 'qDimensionInfo/1' },
                      },
                    },
                  },
                  settings: {
                    x: {
                      scale: 'x',
                      fn: (d) => {
                        const prev = prevData.find((p) => p[0].qElemNumber === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d.scale(prev[2].qNum),
                            d.scale(d.datum.x.value),
                          )(t);
                        }
                        return d.scale(d.datum.x.value);
                      },
                    },
                    y: {
                      scale: 'y',
                      fn: (d) => {
                        const prev = prevData.find((p) => p[0].qElemNumber === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d.scale(prev[3].qNum),
                            d.scale(d.datum.y.value),
                          )(t);
                        }
                        return d.scale(d.datum.y.value);
                      },
                    },
                    shape: 'circle',
                    size: {
                      fn: (d) => {
                        const domain = d3.extent(d.data.items.map((datum) => datum.size.value));
                        domain[0] = 1;
                        const size = d3.scaleLog().domain(domain).range([0, 1])(d.datum.size.value);
                        const prev = prevData.find((p) => p[0].qElemNumber === d.datum.value);
                        if (prev) {
                          return interpolate(
                            d3.scaleLog().domain(domain).range([0, 1])(prev[4].qNum),
                            size,
                          )(t);
                        }
                        return interpolate(0, size)(t);
                      },
                    },
                    strokeWidth: (d) => {
                      if (selectedOutlet.length) {
                        if (selectedOutlet.includes(d.datum.label)) {
                          return 2;
                        }
                        return 1;
                      }
                      return 1;
                    },
                    stroke: (d) => {
                      if (selectedOutlet.length) {
                        if (selectedOutlet.includes(d.datum.label)) {
                          return '#28A745';
                        }
                        return '#CCCCCC';
                      }
                      return '#CCCCCC';
                    },
                    opacity: (d) => {
                      if (selectedOutlet.length) {
                        if (selectedOutlet.includes(d.datum.label)) {
                          return 1;
                        }
                        return 0.5;
                      }
                      return 0.8;
                    },
                    fill: (d) => {
                      const value = d.datum.y.value - d.datum.x.value;
                      const scaledValue = (value + 1) / 2;
                      if (selectedOutlet.length) {
                        if (selectedOutlet.includes(d.datum.label)) {
                          return d3.interpolateRdBu(scaledValue);
                        }
                        return '#D3D3D3';
                      }
                      return d3.interpolateRdBu(scaledValue);
                    },
                  },
                },
                {
                  key: 'tooltip',
                  type: 'tooltip',
                  displayOrder: 10,
                  settings: {
                    duration: 15000,
                    // Target point marker
                    filter: (nodes) => nodes.filter((node) => node.type === 'circle'),
                    // Extract data
                    extract: ({ node }) => {
                      const obj = {};
                      obj.fill = node.attrs.fill;
                      obj.outlet = node.data.label;
                      obj.outlet_pretty_name = node.data.outlet_pretty_name.label;
                      obj.staff_non = node.data.x.label;
                      obj.supervisor_non = node.data.y.label;
                      return obj;
                    },
                    content: ({ h, data }) => { // eslint-disable-line no-shadow
                      const els = [];

                      data.forEach((node) => {
                        const eltitle = h('div', {
                          style: {
                            backgroundColor: node.fill, padding: '5px 10px', textAlign: 'left',
                          },
                        }, [h('span', {
                          style: {
                            color: lightOrDark(node.fill) === 'light' ? '#5C5C5C' : '#FFFFFF', textTransform: 'uppercase', fontWeight: 600, fontSize: 14,
                          },
                        }, node.outlet_pretty_name)]);

                        const el1 = h('td', { style: { padding: 4, fontWeight: 600 } }, `${genderToggled ? 'Supervisor Women %' : 'Supervisor Vis.Min/Indig. %'}`);
                        const el2 = h('td', { style: { padding: 4 } }, node.supervisor_non);

                        const el3 = h('td', { style: { padding: 4, fontWeight: 600 } }, `${genderToggled ? 'Staff Women %' : 'Staff Vis.Min/Indig. %'}`);
                        const el4 = h('td', { style: { padding: 4 } }, node.staff_non);

                        const elr1 = h('tr', { style: { padding: 4 } }, [el1, el2]);
                        const elr2 = h('tr', { style: { padding: 4 } }, [el3, el4]);

                        els.push(eltitle, elr1, elr2);
                      });
                      return h('div', { style: { padding: 5 } }, els);
                    },
                    placement: {
                      type: 'pointer',
                      area: 'target',
                      dock: 'auto',
                    },
                  },
                },
              ],
              interactions: [
                {
                  type: 'native',
                  events: {
                    mousemove(e) {
                      this.chart.component('tooltip').emit('show', e);
                    },
                    mouseleave() {
                      this.chart.component('tooltip').emit('hide');
                    },
                  },
                },
                {
                  type: 'hammer',
                  gestures: [
                    {
                      type: 'Tap',
                      events: {
                        tap(e) {
                          const chartBounds = element.getBoundingClientRect();
                          const cx = e.center.x - chartBounds.left;
                          const cy = e.center.y - chartBounds.top;
                          const shapes = this.chart.shapesAt({ x: cx, y: cy });
                          const values = shapes.map((shape) => shape.data.value);
                          model.selectHyperCubeValues('/qHyperCubeDef', 0, values, true);
                        },
                      },
                    },
                  ],
                },
              ],
            },
          });
          if (t === 1) {
            stopTransition();
            setPrevData(layout.qHyperCube.qDataPages[0].qMatrix);
          }
        });
      }, [layout, instance, rect.width, rect.height]);

      useEffect(() => {
        if (!instance) {
          return;
        }
        instance.update();
      }, [rect.width, rect.height, instance]);
    },
  };
}
